/**
 * GoogleAddressMap
 * 
 * Componente de mapa interactivo de Google Maps para selección de direcciones.
 * Permite doble click en el mapa o arrastrar un marcador para seleccionar ubicaciones.
 * 
 * DEPENDENCIAS PARA REUTILIZAR:
 * - npm: @react-google-maps/api @mui/material mobx mobx-react
 * 
 * ADAPTACIONES NECESARIAS:
 * 1. Proveedor de API key: Implementar useApplication() que retorne un objeto con googleMapsApiKey()
 *    O directamente reemplazar application.googleMapsApiKey() por tu API key
 * 
 * 2. GoogleAddressMapFeature: El feature que maneja la lógica (ver GoogleAddressMapFeature.js)
 * 
 * USO:
 *   const mapFeature = useFeature(() => new GoogleAddressMapFeature(
 *       miForm, 'address', 'jurisdiction', 'city', 'postalCode'
 *   ));
 *   
 *   <GoogleAddressMap feature={mapFeature} />
 */
import React, { useEffect } from 'react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { Box, CircularProgress } from '@mui/material';
import { observer } from 'mobx-react';
import { useApplication } from '../../ApplicationProvider';

const libraries = ['places', 'geocoding'];

const containerStyle = {
  width: '100%',
  height: '400px',
  borderRadius: '8px',
};

const DEFAULT_CENTER_BUENOS_AIRES = {
  lat: -34.6037, 
  lng: -58.3816,
};

function GoogleAddressMap({feature, form, fieldName = 'address'}) {
  const application = useApplication();

  const readonly = form && fieldName ? form.isReadonly(fieldName) : false;

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: application.googleMapsApiKey(),
    libraries: libraries,
  });

  useEffect(() => {
    if (feature) {
      feature.setIsGoogleMapsLoaded(isLoaded);
    }
  }, [isLoaded, feature]);

  useEffect(() => {
    if (isLoaded && window.google && feature) {
      const geocoder = new window.google.maps.Geocoder();
      feature.setGeocoder(geocoder);
      
      const address = feature.addressField.value || '';
      const jurisdiction = feature.jurisdictionField.value || '';
      const city = feature.cityField.value || '';
      
      if (address && (city || jurisdiction) && !feature.markerPosition) {
        feature.geocodeAddress(address, jurisdiction, city);
      }
    }
  }, [isLoaded, feature]);

  const onMapLoad = (mapInstance) => {
    feature.setMap(mapInstance);
    if (feature.markerPosition) {
      mapInstance.setCenter(feature.markerPosition);
      mapInstance.setZoom(15);
    }
  };

  const handleLocationChange = (event) => {
    if (readonly) return;

    if (!event.latLng) return;

    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    feature.setMarkerPosition({ lat, lng });
    feature.reverseGeocode(lat, lng);
  };

  if (!feature) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box p={2} textAlign="center">
        Error al cargar el mapa. Por favor, verifica que la API key de Google Maps esté configurada.
      </Box>
    );
  }

  if (!isLoaded) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  const markerPosition = feature.markerPosition;
  const mapCenter = markerPosition || DEFAULT_CENTER_BUENOS_AIRES;
  const mapZoom = markerPosition ? 15 : 10;

  return (
    <Box mt={2} mb={2}>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={mapCenter}
        zoom={mapZoom}
        onLoad={onMapLoad}
        onDblClick={handleLocationChange}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
          disableDoubleClickZoom: !readonly,
          draggable: true,
          gestureHandling: readonly ? 'none' : 'auto',
          styles: [],
          keyboardShortcuts: !readonly,
          clickableIcons: !readonly,
        }}
      >
        {markerPosition && markerPosition.lat && markerPosition.lng && (
          <Marker
            position={markerPosition}
            draggable={!readonly}
            onDragEnd={handleLocationChange}
            title="Arrastra el marcador para actualizar la dirección"
            key={`marker-${markerPosition.lat}-${markerPosition.lng}`}
            opacity={readonly ? 0.8 : 1}
          />
        )}
      </GoogleMap>
    </Box>
  );
}

export default observer(GoogleAddressMap);

